import React, { useEffect, useRef, useState } from 'react';

interface DropdownProps<T extends string | number> {
  options?: Array<{ value: T; label: string }>;
  value?: T[] | T;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  name?: string;
  multiple?: boolean;
  insideForm?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  isOpen?: boolean;
  onToggle?: () => void;
}

const Dropdown = <T extends string | number>({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  className = '',
  disabled = false,
  name = '',
  multiple = false,
  insideForm = false,
  icon,
  children,
  isOpen: isOpenProp,
  onToggle,
}: DropdownProps<T>) => {

  const [isOpen, setIsOpen] = useState(
    isOpenProp !== undefined ? isOpenProp : false
  );
  const [iconColor, setIconColor] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    // Toggle internal state and icon color
    const newState = !isOpen;
    setIconColor(newState ? "opened-color" : "");

    // Call onToggle callback from parent if provided
    if (onToggle) { onToggle(); }
    else { setIsOpen(newState); }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (isOpen) {
          if (onToggle) { onToggle(); }
          else { setIsOpen(false); }
          setIconColor("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  // Synchronize internal state with the prop if isOpen is controlled by parent
  useEffect(() => {
    if (isOpenProp !== undefined && isOpenProp !== isOpen) {
      setIsOpen(isOpenProp);
      setIconColor(isOpenProp ? "opened-color" : "");
    }
    //eslint-disable-next-line
  }, [isOpenProp]);

  const renderIcon = () => {
    // Check if the icon is a valid React element (custom component)
    if (React.isValidElement(icon)) {
      const element = icon as React.ReactElement;
      if (element.type && typeof element.type === 'function') {
        return React.cloneElement(element, { isOpen });
      }
      return icon;
    }
    return icon;
  };

  return (
    insideForm ? <select
      className={`form-select ${className}`}
      name={name}
      value={multiple ? (value as string[]) : (value as string | number)} // Force `value` to be string[] when `multiple` is true
      onChange={onChange}
      disabled={disabled}
      aria-label="Dropdown select"
      multiple={multiple}
    >
      {!multiple && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}

      {options && options.map((option) => (
        <option key={option.value as unknown as string} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
      :
      <div className="dropdown" ref={dropdownRef}>
        <div
          onClick={toggleDropdown}
          className={` ${iconColor}`}
          role="button"
          aria-expanded={isOpen}
        >
          {renderIcon()}
        </div>
        {isOpen && (
          <div className={`dropdown-content ${className}`} role="menu">
            {children}
          </div>
        )}
      </div>

  );
};

export default Dropdown;
